import { Box, Card, FileInput, Group, Paper, SegmentedControl, Select, Stack, Switch, Text } from "@mantine/core";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { DataTable, type DataTableColumn, type DataTableSortStatus } from "mantine-datatable";
import { type DragEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EDITOR_STYLE } from "../../common/editor-theme";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { UtilityTitle } from "../../common/utility-title";
import { AUTO, DELIMITERS, isDelimiter } from "./delimiters";
import { editorExtensions } from "./editor";
import { FILE_TOO_BIG, MAX_FILE_BYTES, raggedWarning, READ_FAILED, summarise, truncatedMessage, UNTERMINATED_QUOTE } from "./messages";
import { readCsv } from "./parse";
import { cellText, MAX_ROWS, sortRows } from "./rows";
import { SAMPLE_DOCUMENT } from "./sample";
import { DEFAULT_VIEW, isView, VIEWS } from "./views";

// The grid's own stylesheet, imported here so it rides this page's chunk rather than the one every visitor loads.
import "mantine-datatable/styles.css";

export default function Csv() {
  const initialState = useInitialHashState<{
    value?: string;
    delimiter?: string;
    header?: boolean;
    view?: string;
  }>();

  // Uncontrolled, as in /json: feeding the text back in would replace the document and send the caret to the end.
  const [initialValue] = useState(() => initialState?.value ?? SAMPLE_DOCUMENT);
  const valueRef = useRef(initialValue);
  const viewRef = useRef<EditorView | null>(null);

  const [delimiter, setDelimiter] = useState(() =>
    isDelimiter(initialState?.delimiter) ? initialState.delimiter : AUTO
  );
  const [header, setHeader] = useState(initialState?.header ?? true);
  const [view, setView] = useState(() => isView(initialState?.view) ? initialState.view : DEFAULT_VIEW);
  // What the table was last read from, which trails the typing by a beat: splitting a long document on every
  // keystroke is the one thing here that would make the editor feel slow.
  const [text, setText] = useState(initialValue);
  const parseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // What a file the browser would not read left behind, said where the grid would otherwise sit empty.
  const [problem, setProblem] = useState<string | null>(null);
  // Which column the grid is being read in the order of, which is nothing until a heading is clicked.
  const [sort, setSort] = useState<DataTableSortStatus<Row> | null>(null);

  const table = useMemo(() => readCsv(text, delimiter, header), [text, delimiter, header]);

  // A sort is over a column of this reading of the document, so a delimiter or a header row that renames the
  // columns takes it off rather than leaving the grid in the order of a column that is no longer there.
  useEffect(() => setSort(null), [delimiter, header]);

  const syncShareState = useRegisterShareState(() => ({
    value: valueRef.current,
    delimiter,
    header,
    view,
  }));

  // `window.csvEditor` is how devtools and the Playwright spec read the document; drop it on the way out, with
  // whatever the last keystroke left waiting to be read.
  useEffect(() => () => {
    self.csvEditor = undefined;
    if (parseTimer.current !== null) clearTimeout(parseTimer.current);
  }, []);

  const handleChange = useCallback((next: string) => {
    valueRef.current = next;
    syncShareState();

    if (parseTimer.current !== null) clearTimeout(parseTimer.current);
    parseTimer.current = setTimeout(() => {
      parseTimer.current = null;
      setText(valueRef.current);
    }, PARSE_DELAY);
  }, [syncShareState]);

  // Dispatched rather than assigned, which puts the file on the editor's own undo stack: what it wrote over is
  // still one Ctrl+Z away, and the change handler it runs is what carries the text to the ref and the URL.
  const placeDocument = useCallback((next: string) => {
    const editor = viewRef.current;
    if (!editor) return;
    editor.dispatch({ changes: { from: 0, to: editor.state.doc.length, insert: next } });
  }, []);

  // A file is read into the editor and then forgotten: the document is the thing the page works on, so an upload
  // and a paste arrive at exactly the same state and neither is a source the rest of the page has to know about.
  const handleFile = useCallback(async (file: File | null) => {
    if (!file) return;

    if (file.size > MAX_FILE_BYTES) {
      setProblem(FILE_TOO_BIG);
      return;
    }

    try {
      const read = await file.text();
      setProblem(null);
      placeDocument(read);
      // The debounce is for typing; a file arrives whole and there is nothing to wait for.
      if (parseTimer.current !== null) clearTimeout(parseTimer.current);
      parseTimer.current = null;
      setText(read);
    } catch {
      setProblem(READ_FAILED);
    }
  }, [placeDocument]);

  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    void handleFile(event.dataTransfer.files.item(0));
  }, [handleFile]);

  const rows = useMemo(() => {
    const at = sort ? Number(sort.columnAccessor) : -1;
    return at >= 0 ? sortRows(table.rows, at, sort!.direction) : table.rows;
  }, [sort, table.rows]);

  const records = useMemo<Row[]>(
    () => rows.slice(0, MAX_ROWS).map((row, index) => ({ [ROW_KEY]: index, cells: row })),
    [rows],
  );

  const columns = useMemo<DataTableColumn<Row>[]>(
    () =>
      table.columns.map((title, at) => ({
        // The position and never the name: two columns called the same thing are two columns.
        accessor: String(at),
        title,
        sortable: true,
        resizable: true,
        ellipsis: true,
        render: (record) => <Cell text={cellText(record.cells, at)} />,
      })),
    [table.columns],
  );

  return (
    <Stack flex={1} className="fill-screen" gap="md">
      <UtilityTitle
        directory="csv"
        control={
          <SegmentedControl
            value={view}
            onChange={(value) => isView(value) && setView(value)}
            aria-label="Which half of the page is showing"
            data={VIEWS.map(({ value, label, Icon }) => ({
              value,
              label: (
                <Group gap={6} wrap="nowrap" justify="center">
                  <Icon size="1rem" stroke={1.5} />
                  {label}
                </Group>
              ),
            }))}
          />
        }
      >
        CSV
      </UtilityTitle>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Box className="settings-row">
            <Select
              label="Delimiter"
              data={DELIMITERS.map(({ value, label }) => ({ value, label }))}
              value={delimiter}
              onChange={(value) => isDelimiter(value) && setDelimiter(value)}
              allowDeselect={false}
            />
            <Box
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={(event) => {
                // Dragleave bubbles up from the input inside, so only leaving the box itself calls the drag off.
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
              }}
              onDrop={handleDrop}
              style={{
                borderRadius: "var(--mantine-radius-sm)",
                outline: dragging ? "2px dashed var(--mantine-color-blue-5)" : "none",
                outlineOffset: "4px",
              }}
            >
              <FileInput
                label="File"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                placeholder="Choose a file, or drop one here"
                aria-label="CSV file to read"
                value={null}
                onChange={(file) => void handleFile(file)}
              />
            </Box>
            {/* Bottom-aligned by the row, and nudged up to the middle of the inputs beside it. */}
            <Box pb={8}>
              <Switch
                checked={header}
                onChange={(event) => setHeader(event.currentTarget.checked)}
                label="First row is a header"
              />
            </Box>
          </Box>

          <Group gap="sm" wrap="nowrap">
            <Text size="sm" c="dimmed">{summarise(table, rows.length)}</Text>
            {table.unterminated && <Text size="sm" c="yellow">{UNTERMINATED_QUOTE}</Text>}
            {table.ragged > 0 && <Text size="sm" c="yellow">{raggedWarning(table.ragged)}</Text>}
            {problem && <Text size="sm" c="red">{problem}</Text>}
          </Group>
        </Stack>
      </Card>

      {
        /* Both halves stay mounted whichever view is showing: unmounting the editor would take the document, the
          undo history and the caret with it, and a view is a way of looking at the page rather than a different one. */
      }
      <Box className="csv-panes" data-view={view}>
        <Paper withBorder shadow="sm" radius="md" className="csv-text-pane">
          <Box style={PANE_FILL}>
            <CodeMirror
              value={initialValue}
              // A percentage height only resolves against a definite one, so the wrapper matches the editor.
              height="100%"
              style={EDITOR_STYLE}
              theme="dark"
              extensions={editorExtensions(table.delimiter)}
              onCreateEditor={(editor) => {
                viewRef.current = editor;
                self.csvEditor = editor;
              }}
              onChange={handleChange}
            />
          </Box>
        </Paper>

        <Paper withBorder shadow="sm" radius="md" className="csv-table-pane">
          <Box style={PANE_FILL}>
            {rows.length > MAX_ROWS && (
              <Text size="xs" c="yellow" px="sm" py={6} style={BAR}>{truncatedMessage(rows.length)}</Text>
            )}
            <Box style={{ flex: 1, minHeight: 0 }}>
              <DataTable
                records={records}
                columns={columns}
                idAccessor={ROW_KEY}
                height="100%"
                sortStatus={sort ?? UNSORTED}
                onSortStatusChange={setSort}
                withColumnBorders
                striped
                highlightOnHover
                noRecordsText="Nothing to show — paste a delimited document above, or drop a file on the picker."
                verticalSpacing={4}
                horizontalSpacing="sm"
              />
            </Box>
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
}

// A row of the grid: the fields as the document has them, under a key that is the position and not a column.
interface Row {
  [ROW_KEY]: number;
  cells: string[];
}

// A span rather than Mantine's default paragraph, since the cell's own ellipsis has nothing to clip a block with.
function Cell({ text }: { text: string }) {
  return <Text component="span" size="xs" ff="monospace" title={text}>{text}</Text>;
}

// Long enough that a run of keystrokes rebuilds the table once, short enough to read as the next thing that happens.
const PARSE_DELAY = 150;

// The record key the grid identifies a row by, which is a position and never a column of the document.
const ROW_KEY = "__row";

// What the grid is handed while no heading has been clicked: it takes no null, and an accessor no column answers to
// is the plainest way of saying that the rows are in the order the document has them.
const UNSORTED: DataTableSortStatus<Row> = { columnAccessor: "", direction: "asc" };

// A column filling the pane, so a warning holds the top and the grid below it takes the rest and scrolls in it.
const PANE_FILL = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  flexDirection: "column" as const,
};

// A line across the top of the table pane, which the row cap is the only thing to take a turn at.
const BAR = { flex: "none", borderBottom: "1px solid var(--mantine-color-default-border)" };

declare global {
  var csvEditor: EditorView | undefined;
}
